/**
 * Feature flag — v2.5.5 Deal Workspace rollout
 * --------------------------------------------
 * All new "Deal Workspace" UX (workspace header inside Bid Requests, workflow
 * stepper, Bid Requests-tab demotion to More dropdown, Company Branding card,
 * one-click change-order emails) ships behind this single gate so live
 * production users continue to see the old DrawReady UI while the owner
 * dogfoods the new flow.
 *
 * The gate is intentionally simple — when we're ready to ship to everyone we
 * just return `true` here. No flag system, no flag-flag-of-flags.
 */
export const V255_GATED_EMAILS = ['modteetsw@gmail.com'];

/** Does this user have the v2.5.5 Deal Workspace rollout enabled? */
export function isV255User(user) {
  if (!user) return false;
  // Public "Try the demo" visitors always see the current (v2.5.5) product —
  // the demo should mirror exactly what the live environment ships.
  const id = (user.id || '').toLowerCase();
  if (id.startsWith('demo-')) return true;
  const email = (user.email || user.user_email || '').toLowerCase().trim();
  if (!email) return false;
  return V255_GATED_EMAILS.includes(email);
}

/**
 * Advanced-module add-on gate.
 * The base DrawReady experience is pared down to the core "get a draw funded"
 * flow. Advanced modules (Schedule, Project Hub, Timeline, Spec Sheets, Paint
 * Colors, Reports, Financial Analysis, Contractor Portal) are positioned as
 * paid add-ons. Only the owner account has them unlocked today; every other
 * user sees the upgrade screen. Flip an email in ADVANCED_MODULE_EMAILS to
 * grant access, or return true here to ship to everyone.
 */
export const ADVANCED_MODULE_EMAILS = ['modteetsw@gmail.com'];

export function hasAdvancedModules(user) {
  if (!user) return false;
  const email = (user.email || user.user_email || '').toLowerCase().trim();
  return ADVANCED_MODULE_EMAILS.includes(email);
}

// Per-module add-on entitlements. Owner email has everything; otherwise a module
// is unlocked when its id is in the user's purchased `addons` list.
export const ADDON_CATALOG = [
  { id: 'project-hub', name: 'Project Hub', price: 19, description: 'Contractors, bid requests, documents & reviews in one command center.' },
  { id: 'schedule', name: 'Schedule', price: 19, description: 'A living construction calendar across every funded project.' },
  { id: 'reports', name: 'Reports', price: 19, description: 'Board-ready draw reports and portfolio exports on demand.' },
  { id: 'project-tools', name: 'Project Tools', price: 19, description: 'Timeline, Spec Sheets & Paint Colors inside each project.' },
];

export function ownsAddon(user, moduleId) {
  if (!user) return false;
  const email = (user.email || user.user_email || '').toLowerCase().trim();
  if (ADVANCED_MODULE_EMAILS.includes(email)) return true;
  // Add-ons are à la carte on Basic/Pro/Advanced, but ALL included with Enterprise.
  const tier = (user.subscription_tier || '').toLowerCase();
  if (tier === 'enterprise') return true;
  return Array.isArray(user.addons) && user.addons.includes(moduleId);
}

/**
 * Contractor Payment Report — a Pro-included feature (also Enterprise). Not a
 * $7 add-on. Owner + free-access review accounts always have it.
 */
export function hasContractorPaymentReport(user) {
  if (!user) return false;
  const email = (user.email || user.user_email || '').toLowerCase().trim();
  if (ADVANCED_MODULE_EMAILS.includes(email)) return true;
  if (user.is_free_access) return true;
  const tier = (user.subscription_tier || '').toLowerCase();
  return ['pro', 'enterprise'].includes(tier);
}
